import { FC, useEffect, useMemo } from 'react';
import { Modal, FileInput, BaseImageList } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { Delete } from '@mui/icons-material';

interface Values {
  example: File | undefined;
}

interface Props extends ModalProps {
  initialExample?: File;
  closeModal: (
    param?:
      | { action: 'CLOSE' }
      | { action: 'CONFIRM'; example: File | undefined },
  ) => void;
}

export const ExampleFileModal: FC<Props> = ({ initialExample, closeModal }) => {
  const { t } = useTranslation();

  const { control, handleSubmit, watch } = useForm<Values>({
    defaultValues: { example: initialExample },
  });

  const [example] = watch(['example']);

  const exampleSrc = useMemo(
    () => (example ? URL.createObjectURL(example) : undefined),
    [example],
  );

  useEffect(() => {
    return () => {
      if (exampleSrc) {
        URL.revokeObjectURL(exampleSrc);
      }
    };
  }, [exampleSrc]);

  const onSubmit: SubmitHandler<Values> = (values) => {
    closeModal({
      action: 'CONFIRM',
      example: values.example,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('ai.actions.upload_example')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
          },
        ]}
      >
        <Controller
          name="example"
          control={control}
          render={({ field: { name, onChange, ref } }) =>
            exampleSrc ? (
              <BaseImageList
                items={[
                  {
                    src: exampleSrc,
                    actions: [
                      {
                        icon: Delete,
                        variant: 'neutral-outline',
                        onClick: () => onChange(''),
                      },
                    ],
                  },
                ]}
                cols={1}
              />
            ) : (
              <FileInput
                name={name}
                onChange={([file]) => {
                  onChange(file);
                }}
                accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                maxFiles={1}
                ref={ref}
              />
            )
          }
        />
      </Modal.Content>
    </form>
  );
};
